import { useState } from "react";
import { supabase } from "../lib/supabase";
import { Loader2, Bot, Mail, ArrowLeft, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError || !data.session) {
      setError("Email ou mot de passe incorrect.");
      setLoading(false);
      return;
    }

    // Verify the user exists in portail_users before accepting the login
    const res = await fetch("/api/portail/me", {
      headers: { Authorization: `Bearer ${data.session.access_token}` },
    });

    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      if (json.inactive) {
        setError("Votre compte a été désactivé. Contactez Jobotto.");
      } else {
        setError("Accès non autorisé. Ce compte n'est pas lié à un espace partenaire.");
      }
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // Auth-context onAuthStateChange will fire and update React state automatically.
    // No window.location.href needed — React re-renders and shows Dashboard.
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (resetError) {
      setError(resetError.message);
    } else {
      setResetSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo + titre */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20">
            <Bot className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Espace Partenaire Jobotto</h1>
            <p className="text-sm text-muted-foreground mt-1">Accès réservé aux partenaires et revendeurs agréés</p>
          </div>
        </div>

        {/* Carte */}
        <div className="bg-card border border-border/50 rounded-2xl shadow-xl shadow-black/5 overflow-hidden">
          {forgotMode ? (
            <div className="p-6 space-y-5">
              <div>
                <h2 className="font-semibold text-lg">Mot de passe oublié</h2>
                <p className="text-sm text-muted-foreground mt-1">Un lien de réinitialisation sera envoyé à votre adresse email.</p>
              </div>
              {resetSent ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <Mail className="w-5 h-5 text-green-600 shrink-0" />
                    <p className="text-sm text-green-700 dark:text-green-400">Email envoyé à <strong>{resetEmail}</strong>. Vérifiez votre boîte de réception.</p>
                  </div>
                  <button
                    onClick={() => { setForgotMode(false); setResetSent(false); setResetEmail(""); setError(null); }}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="w-4 h-4" /> Retour à la connexion
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5">
                      <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">Adresse email</label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      placeholder="votre@email.fr"
                      required
                      className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-primary text-primary-foreground rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-60"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Envoyer le lien de réinitialisation
                  </button>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(false); setError(null); }}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" /> Retour
                  </button>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleLogin} className="p-6 space-y-5">
              <div>
                <h2 className="font-semibold text-lg">Connexion</h2>
                <p className="text-sm text-muted-foreground mt-1">Accès réservé aux partenaires Jobotech</p>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5" data-testid="text-portail-error">
                  <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="portail-email">Adresse email</label>
                <input
                  id="portail-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="votre@email.fr"
                  required
                  className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-portail-email"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground" htmlFor="portail-password">Mot de passe</label>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(true); setError(null); }}
                    className="text-xs text-primary hover:underline"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <input
                  id="portail-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-portail-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary text-primary-foreground rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 disabled:opacity-60"
                data-testid="button-portail-login"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Se connecter
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Jobotech — Espace Partenaires · <a href="mailto:contact@jobotto.fr" className="hover:underline">contact@jobotto.fr</a>
        </p>
      </div>
    </div>
  );
}
